import React, {useCallback, useEffect, useState} from 'react';
import {View, StyleSheet, Alert, FlatList} from 'react-native';
import {
  Text,
  ActivityIndicator,
  Dialog,
  Portal,
  Button,
  TextInput,
  IconButton,
  Snackbar,
  Divider,
  FAB,
  Chip,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation} from '@react-navigation/native';
import {priceBookApi} from '../../services/api/priceBookApi';
import GradientHeader from '../../components/common/GradientHeader';
import {colors, spacing} from '../../theme';

const BOOK_TYPES = [
  {value: 'BASE', label: 'Base'},
  {value: 'ORG_OVERRIDE', label: 'Org Override'},
  {value: 'B2B_CONTRACT', label: 'B2B Contract'},
];

const PriceBookListScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();

  const [priceBooks, setPriceBooks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [bookType, setBookType] = useState('BASE');
  const [currency, setCurrency] = useState('');
  const [priority, setPriority] = useState('0');
  const [snackbar, setSnackbar] = useState('');

  const fetchPriceBooks = useCallback(async () => {
    try {
      const res = await priceBookApi.listPriceBooks();
      const books = res.data.data || [];
      books.sort((a, b) => {
        if (a.status === b.status) return 0;
        return a.status === 'ACTIVE' ? -1 : 1;
      });
      setPriceBooks(books);
    } catch (e) {
      setSnackbar(e.response?.data?.error?.message || t('common.error'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchPriceBooks();
  }, [fetchPriceBooks]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchPriceBooks();
    setRefreshing(false);
  }, [fetchPriceBooks]);

  const openAddDialog = useCallback(() => {
    setEditingItem(null);
    setCode('');
    setName('');
    setBookType('BASE');
    setCurrency('');
    setPriority('0');
    setDialogVisible(true);
  }, []);

  const openEditDialog = useCallback(item => {
    setEditingItem(item);
    setCode(item.code || '');
    setName(item.name);
    setBookType(item.bookType);
    setCurrency(item.currency || '');
    setPriority(String(item.priority ?? 0));
    setDialogVisible(true);
  }, []);

  const handleSave = useCallback(async () => {
    const trimmedCode = code.trim();
    const trimmedName = name.trim();
    if (!trimmedName) return;
    if (!editingItem && !trimmedCode) return;

    try {
      const isDefaultBook = editingItem && editingItem.code === 'DEFAULT';
      const data = {
        name: trimmedName,
        bookType,
        currency: currency.trim() || null,
        priority: parseInt(priority, 10) || 0,
      };
      if (!isDefaultBook && trimmedCode) {
        data.code = trimmedCode;
      }

      if (editingItem) {
        await priceBookApi.updatePriceBook(editingItem.id, data);
      } else {
        await priceBookApi.createPriceBook(data);
      }
      setDialogVisible(false);
      await fetchPriceBooks();
    } catch (e) {
      setSnackbar(e.response?.data?.error?.message || t('common.error'));
    }
  }, [code, name, bookType, currency, priority, editingItem, fetchPriceBooks, t]);

  const handleDelete = useCallback(
    item => {
      Alert.alert(t('common.delete'), `Delete "${item.name}"?`, [
        {text: t('common.cancel'), style: 'cancel'},
        {
          text: t('common.delete'),
          style: 'destructive',
          onPress: async () => {
            try {
              await priceBookApi.deletePriceBook(item.id);
              await fetchPriceBooks();
            } catch (e) {
              setSnackbar(
                e.response?.data?.error?.message || t('common.error'),
              );
            }
          },
        },
      ]);
    },
    [fetchPriceBooks, t],
  );

  const renderItem = useCallback(
    ({item}) => {
      const isInactive = item.status === 'INACTIVE';
      return (
      <View style={[styles.row, isInactive && styles.rowInactive]}>
        <View style={styles.itemInfo}>
          <Text style={[styles.itemName, isInactive && styles.textInactive]}>
            {item.name}
            {isInactive ? ' (Inactive)' : ''}
          </Text>
          <Text style={styles.itemMeta}>
            {item.code || '—'}
            {` · ${item.bookType}`}
            {item.currency ? ` · ${item.currency}` : ''}
            {` · Priority: ${item.priority}`}
          </Text>
        </View>
        <View style={styles.rowActions}>
          <IconButton
            icon="table-edit"
            size={20}
            iconColor={colors.primary}
            onPress={() =>
              navigation.navigate('PriceBookPrices', {priceBook: item})
            }
            style={styles.actionBtn}
          />
          <IconButton
            icon="pencil-outline"
            size={20}
            iconColor={colors.textSecondary}
            onPress={() => openEditDialog(item)}
            style={styles.actionBtn}
          />
          <IconButton
            icon="delete-outline"
            size={20}
            iconColor={colors.textSecondary}
            onPress={() => handleDelete(item)}
            style={styles.actionBtn}
          />
        </View>
      </View>
      );
    },
    [openEditDialog, handleDelete, navigation],
  );

  const keyExtractor = useCallback(item => String(item.id), []);
  const ListEmpty = useCallback(
    () => <Text style={styles.emptyText}>{t('common.noData')}</Text>,
    [t],
  );
  const ItemSeparator = useCallback(() => <Divider />, []);

  if (isLoading && priceBooks.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <GradientHeader title="Price Books" onBack={() => navigation.goBack()} />

      <FlatList
        data={priceBooks}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        ListEmptyComponent={ListEmpty}
        ItemSeparatorComponent={ItemSeparator}
        refreshing={refreshing}
        onRefresh={handleRefresh}
      />

      {/* Add / Edit Dialog */}
      <Portal>
        <Dialog
          visible={dialogVisible}
          onDismiss={() => setDialogVisible(false)}
          style={{borderRadius: 15}}>
          <Dialog.Title>
            {editingItem ? 'Edit Price Book' : 'Add Price Book'}
          </Dialog.Title>
          <Dialog.Content>
            <TextInput
              label="Code"
              value={code}
              onChangeText={setCode}
              mode="outlined"
              maxLength={25}
              disabled={editingItem && editingItem.code === 'DEFAULT'}
              style={styles.dialogInput}
            />
            <TextInput
              label="Name"
              value={name}
              onChangeText={setName}
              mode="outlined"
              style={styles.dialogInput}
            />
            <Text style={styles.fieldLabel}>Book Type</Text>
            <View style={styles.chipRow}>
              {BOOK_TYPES.map(bt => (
                <Chip
                  key={bt.value}
                  selected={bookType === bt.value}
                  selectedColor={bookType === bt.value ? '#fff' : undefined}
                  onPress={() => setBookType(bt.value)}
                  style={[styles.typeChip, bookType === bt.value && styles.typeChipActive]}>
                  <Text style={{fontSize: 13, color: bookType === bt.value ? '#fff' : '#000', marginTop: 3}}>{bt.label}</Text>
                </Chip>
              ))}
            </View>
            <TextInput
              label="Currency"
              value={currency}
              onChangeText={setCurrency}
              mode="outlined"
              style={styles.dialogInput}
            />
            <TextInput
              label="Priority"
              value={priority}
              onChangeText={setPriority}
              mode="outlined"
              keyboardType="numeric"
              style={styles.dialogInput}
            />
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDialogVisible(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              mode="contained"
              onPress={handleSave}
              style={styles.saveButton}>
              {t('common.save')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      <FAB
        icon="plus"
        style={styles.fab}
        onPress={openAddDialog}
        color={colors.white}
      />

      <Snackbar
        visible={!!snackbar}
        onDismiss={() => setSnackbar('')}
        duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  fab: {
    position: 'absolute',
    right: spacing.md,
    bottom: spacing.md,
    backgroundColor: colors.primary,
  },
  emptyText: {
    textAlign: 'center',
    color: colors.textSecondary,
    marginTop: spacing.xxl,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.md,
  },
  rowInactive: {
    opacity: 0.5,
  },
  itemInfo: {flex: 1},
  itemName: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
  },
  textInactive: {
    textDecorationLine: 'line-through',
    color: colors.textSecondary,
  },
  itemMeta: {
    fontSize: 12,
    color: colors.textSecondary,
    marginTop: 2,
  },
  rowActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  actionBtn: {margin: 0},
  dialogInput: {
    marginBottom: spacing.sm,
    backgroundColor: colors.surface,
  },
  fieldLabel: {
    fontSize: 12,
    color: colors.textSecondary,
    marginBottom: spacing.xs,
    marginTop: spacing.xs,
  },
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
    marginBottom: spacing.sm,
  },
  typeChip: {backgroundColor: colors.background},
  typeChipActive: {backgroundColor: colors.primary},
  saveButton: {
    backgroundColor: colors.primary,
    borderRadius: 10,
    paddingHorizontal: 10,
  },
});

export default PriceBookListScreen;
